// 🩺 Health Check — same as the desktop app: runs the analysis (fixed rules on the shop's data +
// the AI report) and lists every finding. Findings with a safe fix have ✓ Accept (applied by the
// server after checking nothing changed), ✗ Reject, and ↶ Undo after accepting.
// Owner only; shown when the platform owner enabled the ai_analysis add-on.
import React, { useEffect, useState } from 'react';
import api from '../../config/api';
import useAddons from '../../hooks/useAddons';

const GREEN = '#2e9f86';
const SEV = {
  high:   { label: 'HIGH',   bg: '#fee2e2', fg: '#b91c1c' },
  medium: { label: 'MEDIUM', bg: '#fef3c7', fg: '#92400e' },
  low:    { label: 'LOW',    bg: '#e0e7ff', fg: '#3730a3' },
  info:   { label: 'INFO',   bg: '#dcfce7', fg: '#166534' },
};
const errOf = (e) => e.response?.data?.error || e.message;
const btn = (bg, fg) => ({ background: bg, color: fg, border: bg === '#fff' ? '1px solid #d1d5db' : 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' });

export default function HealthCheck() {
  const addons = useAddons();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState(null);
  const [arabic, setArabic] = useState(() => (document.documentElement.dir === 'rtl'));
  const [error, setError] = useState('');

  const loadLatest = async () => {
    try { const r = await api.get('/api/analysis/latest'); setRun(r.data || null); setError(''); }
    catch (e) { setError(errOf(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadLatest(); }, []);

  const runNow = async () => {
    setRunning(true); setError('');
    try { const r = await api.post('/api/analysis/run', {}, { timeout: 180000 }); setRun(r.data); }
    catch (e) { setError(errOf(e)); }
    finally { setRunning(false); }
  };
  const act = async (fid, verb) => {
    if (!run) return;
    if (verb === 'undo' && !window.confirm('Undo this fix?')) return;
    setBusy(fid);
    try { await api.post(`/api/analysis/${run._id}/${fid}/${verb}`, {}); await loadLatest(); }
    catch (e) { alert(errOf(e)); }
    finally { setBusy(null); }
  };
  const acceptAll = async () => {
    const open = (run?.findings || []).filter((f) => f.status === 'open' && f.action && f.severity !== 'info');
    if (!open.length) return alert('No fixes to accept.');
    if (!window.confirm(`Accept all ${open.length} fixes shown?`)) return;
    let ok = 0, fail = 0;
    for (const f of open) { try { await api.post(`/api/analysis/${run._id}/${f.id}/apply`, {}); ok++; } catch { fail++; } }
    alert(`${ok} applied${fail ? `, ${fail} could not be applied (changed since the analysis)` : ''}.`);
    loadLatest();
  };

  if (!addons.ai_analysis && !loading && !run)
    return <div style={{ padding: 40, color: '#6b7280' }}>🩺 The Health Check is not active for this shop. Contact MMM to enable it.</div>;

  const score = run ? Math.round(run.score || 0) : null;
  const ai = run?.ai;
  const pick = (o, k) => (arabic && o[`${k}_ar`] ? o[`${k}_ar`] : o[k]) || '';

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1f2937', margin: 0 }}>🩺 Health Check</h1>
          <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>Checks all your items, invoices, stock and cash for mistakes and missed money, then suggests fixes. Nothing changes until you accept.</div>
        </div>
        <button onClick={() => setArabic((a) => !a)} style={btn('#fff', '#374151')}>ع / EN</button>
        <button onClick={acceptAll} disabled={!run || running} style={btn('#fff', '#374151')}>✓ Accept all fixes shown</button>
        <button onClick={runNow} disabled={running} style={{ ...btn(GREEN, '#fff'), padding: '9px 18px', fontSize: 14, opacity: running ? 0.6 : 1 }}>{running ? 'Analysing…' : '▶ Run analysis'}</button>
      </div>
      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 13 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e3eae7', padding: 16, width: 170 }}>
          <div style={{ fontSize: 40, fontWeight: 800, color: score == null ? '#9ca3af' : score >= 80 ? GREEN : score >= 55 ? '#f59e0b' : '#ef4444' }}>{score ?? '—'}</div>
          <div style={{ color: '#6b7280', fontSize: 12 }}>{run?.at ? new Date(run.at).toLocaleString() : 'never run'}</div>
        </div>
        <div dir={arabic ? 'rtl' : 'ltr'} style={{ flex: 1, minWidth: 280, background: '#fff', borderRadius: 14, border: '1px solid #e3eae7', padding: 16, fontSize: 14, color: '#1f2937', whiteSpace: 'pre-wrap' }}>
          {running ? 'Reading every item, invoice, stock movement and shift… then the AI writes the report (up to a minute).'
            : !run ? 'Press “Run analysis”.'
            : ai ? pick(ai, 'summary')
            : `${(run.findings || []).length} findings from the checks.${run.ai_error ? `\n(Written AI report not available: ${run.ai_error})` : ''}`}
        </div>
      </div>

      {(ai?.insights || []).map((i, n) => (
        <Card key={`i${n}`} sev="info" title={'💡 ' + pick(i, 'title')} detail={pick(i, 'detail')} rtl={arabic} />
      ))}
      {(run?.findings || []).map((f) => {
        const hasFix = !!f.action;
        return (
          <Card key={f.id} sev={f.severity} title={f.title} detail={f.detail} dim={f.status === 'rejected'}>
            {f.status === 'applied' ? (<>
              <span style={{ color: GREEN, fontWeight: 700, fontSize: 13 }}>✓ Applied</span>
              <button disabled={busy === f.id} onClick={() => act(f.id, 'undo')} style={btn('#fff', '#374151')}>↶ Undo</button>
            </>) : f.status === 'rejected' ? <span style={{ color: '#9ca3af', fontSize: 12 }}>Dismissed</span>
              : f.severity !== 'info' && (<>
                {hasFix && <button disabled={busy === f.id} onClick={() => act(f.id, 'apply')} style={btn(GREEN, '#fff')}>✓ Accept</button>}
                <button disabled={busy === f.id} onClick={() => act(f.id, 'reject')} style={btn('#fff', '#374151')}>{hasFix ? '✗ Reject' : '✓ Seen'}</button>
              </>)}
          </Card>
        );
      })}
    </div>
  );
}

function Card({ sev, title, detail, dim, rtl, children }) {
  const s = SEV[sev] || SEV.info;
  return (
    <div style={{ background: '#fff', border: '1px solid #e3eae7', borderRadius: 12, padding: 14, marginBottom: 10, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <span style={{ background: s.bg, color: s.fg, fontSize: 10.5, fontWeight: 800, padding: '3px 8px', borderRadius: 6, flexShrink: 0, marginTop: 2 }}>{s.label}</span>
      <div dir={rtl ? 'rtl' : undefined} style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 14.5, color: dim ? '#9ca3af' : '#1f2937' }}>{title}</div>
        {detail && <div style={{ color: '#6b7280', fontSize: 13, marginTop: 3, whiteSpace: 'pre-wrap' }}>{detail}</div>}
      </div>
      {children && <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>{children}</div>}
    </div>
  );
}
